import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';

import { AuthContextType, AuthProviderProps } from './AuthContext.types';
import { useAuth0 } from '@auth0/auth0-react';
import { axiosInstance, axiosReportingInstance } from 'src/api/axios/axiosInstance';
import { AxiosError } from 'axios';
import { signalR } from 'src/signalR';
import { isTokenExpired } from 'src/utils/helpers';
import { getRedirectUri } from 'src/components/AuthWrapper/Auth0Provider';

const VITE_API_PATH = import.meta.env.VITE_API_PATH ?? 'access.wicketsoft.com';
const VITE_LOCAL_API_PATH = import.meta.env.VITE_LOCAL_API_PATH ?? 'localhost:5555';
const VITE_LOCAL_API_PATH_REPORTING = import.meta.env.VITE_LOCAL_API_PATH_REPORTING ?? 'localhost:6666';
const IS_LOCAL = import.meta.env.VITE_IS_LOCAL_API === 'true';

const AuthContext = createContext<AuthContextType | null>(null);

const initSignalR = async (token: string) => {
  try {
    const baseUrl = `${axiosInstance.defaults.baseURL}/notification`;
    await signalR.ensureConnection(baseUrl, token);
  } catch (error) {
    console.error('Failed to connesct to SignalR:', error);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, configAuthData }) => {
  const ldClient = useLDClient();
  const {
    user,
    isAuthenticated: auth0IsAuthenticated,
    logout: auth0Logout,
    getAccessTokenSilently,
    isLoading: auth0IsLoading,
    loginWithRedirect,
  } = useAuth0();

  const getTokenPermissions = (token: string) => {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const permissions = decodedToken.scopes ? JSON.parse(decodedToken.scopes)[0].permissions : decodedToken.permissions;
    return permissions;
  };

  const login = (token: string) => {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const username = decodedToken.sub;
    const client = decodedToken.scopes
      ? JSON.parse(decodedToken.scopes)[0].client
      : window.location.hostname.split('.')[0];
    const permissions = getTokenPermissions(token);
    initSignalR(token);
    ldClient?.identify({
      key: `${client}`,
      kind: 'client',
    });
    setAuthData({
      username,
      isAuthenticated: true,
      permissions,
    });
  };

  const [authData, setAuthData] = useState<Omit<AuthContextType, 'login' | 'logout'>>(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken && isTokenExpired(refreshToken)) {
      return { isAuthenticated: false, username: null, permissions: [] };
    }

    if (refreshToken && token && isTokenExpired(token)) {
      return { isAuthenticated: true, username: null, permissions: [] };
    }

    if (!token) {
      return { isAuthenticated: false, username: null, permissions: [] };
    }

    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const username = decodedToken.sub as string;
    const client = decodedToken.scopes
      ? JSON.parse(decodedToken.scopes)[0].client
      : window.location.hostname.split('.')[0];
    initSignalR(token);
    ldClient?.identify({
      key: `${client}`,
      kind: 'client',
    });
    const permissions = getTokenPermissions(token);
    return {
      isAuthenticated: true,
      username,
      permissions,
    };
  });

  const addBearerAuth0Token = async (token: string) => {
    const slug = window.location.hostname.split('.')[0]; // cannot rely on client name, needs domain slug for auth0 clients

    if (IS_LOCAL) {
      axiosInstance.defaults.baseURL = `http://${VITE_LOCAL_API_PATH}`;
      axiosReportingInstance.defaults.baseURL = `http://${VITE_LOCAL_API_PATH_REPORTING}`;
    } else {
      axiosInstance.defaults.baseURL = `${location.protocol}//${slug}.${VITE_API_PATH}/api`;
      axiosReportingInstance.defaults.baseURL = `${location.protocol}//${slug}.${VITE_API_PATH}/api-reporting`;
    }

    // // Clear existing interceptors to avoid conflicts
    axiosInstance.interceptors.request.clear();
    axiosInstance.interceptors.response.clear();
    axiosReportingInstance.interceptors.request.clear();
    axiosReportingInstance.interceptors.response.clear();

    axiosInstance.interceptors.request.use(
      (config) => {
        config.headers.set('Authorization', `Bearer-Auth0 ${token}`);
        config.headers.set('Client', configAuthData?.client ?? '');
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(error);
      },
    );
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError): Promise<AxiosError> => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const token = await getAuth0Token();
          if (token) {
            error.config?.headers.set('Authorization', `Bearer-Auth0 ${token}`);
          }
        }
        return Promise.reject(error);
      },
    );
    axiosReportingInstance.interceptors.request.use((config) => {
      config.headers.set('Authorization', `Bearer-Auth0 ${token}`);
      config.headers.set('Client', configAuthData?.client ?? '');
      return config;
    });
    axiosReportingInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError): Promise<AxiosError> => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const token = await getAuth0Token();
          if (token) {
            error.config?.headers.set('Authorization', `Bearer-Auth0 ${token}`);
          }
        }
        return Promise.reject(error);
      },
    );
  };

  const getAuth0Token = async () => {
    try {
      const auth0Token = await getAccessTokenSilently();
      await addBearerAuth0Token(auth0Token);
      login(auth0Token);

      return auth0Token;
    } catch (error) {
      console.error(error);
    }
  };

  const logoutFromAuth0 = () => {
    if (auth0IsAuthenticated) {
      auth0Logout({
        logoutParams: {
          returnTo: getRedirectUri(),
        },
      });
    }
  };

  const logout = () => {
    logoutFromAuth0();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    signalR.stop();
    setAuthData({
      isAuthenticated: false,
      username: null,
      permissions: [],
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken && token && isTokenExpired(token)) {
      login(token);
    }
  }, []);

  useEffect(() => {
    if (auth0IsAuthenticated) {
      getAuth0Token();
    }
  }, [user?.sub, auth0IsAuthenticated]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const error = searchParams.get('error');
    const code = searchParams.get('code');
    const isLoginPage = window.location.pathname === '/auth0-callback';

    if (
      configAuthData?.platform === 'Auth0' &&
      !auth0IsLoading &&
      isLoginPage &&
      !authData.isAuthenticated &&
      !auth0IsAuthenticated &&
      !error &&
      !code
    ) {
      loginWithRedirect();
    }
  }, [authData.isAuthenticated, configAuthData?.platform, auth0IsLoading, auth0IsAuthenticated]);

  return <AuthContext.Provider value={{ ...authData, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
